import { useState, type ReactNode } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { History, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Shared collapsible "past runs" shell (Task #1633) — the same UX as the Ads
 * Express "Recent generations" card: a History-icon header with a count badge
 * that toggles the body open/closed. Renders nothing at all when the history
 * is confirmed empty so brands with no prior runs see no clutter.
 */
export function PastRunsSection({
  title,
  count,
  isLoading,
  testIdPrefix,
  children,
}: {
  title: string;
  count: number;
  isLoading: boolean;
  testIdPrefix: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  if (!isLoading && count === 0) return null;

  return (
    <Card className="p-5 space-y-3" data-testid={`panel-${testIdPrefix}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid={`button-toggle-${testIdPrefix}`}
      >
        <h2 className="font-semibold flex items-center gap-2 text-sm">
          <History className="h-4 w-4 text-primary" /> {title}
          {!isLoading && (
            <Badge variant="secondary" className="text-[10px]" data-testid={`badge-${testIdPrefix}-count`}>
              {count}
            </Badge>
          )}
        </h2>
        {open ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {!open ? null : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 rounded-lg" />
          <Skeleton className="h-14 rounded-lg" />
        </div>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </Card>
  );
}

export function formatRunWhen(iso: string | Date | null | undefined): string {
  if (!iso) return "Unknown date";
  const d = new Date(iso as any);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}
